<template>
    <div class="home-container">
        <!-- 顶部导航栏 -->
        <nav class="navbar">
            <!-- 顶部导航栏 logo 修改 -->
            <div class="nav-brand">
                <img :src="logoUrl" alt="全域AI" class="logo" />
            </div>


            <div class="nav-menu">
                <router-link v-for="item in navItems" :key="item.path" :to="item.path" class="nav-item"
                    :class="{ active: $route.path === item.path }">
                    {{ item.name }}
                </router-link>
            </div>

            <div class="nav-actions">
                <template v-if="isLoggedIn">
                    <el-dropdown @command="handleUserCommand">
                        <div class="user-info">
                            <el-avatar :size="32" :src="userAvatar" />
                            <span class="username">{{ username }}</span>
                            <el-icon class="arrow">
                                <ArrowDown />
                            </el-icon>
                        </div>
                        <template #dropdown>
                            <el-dropdown-menu>
                                <el-dropdown-item command="modifyPassword">
                                    <el-icon>
                                        <Edit />
                                    </el-icon>
                                    <span>修改密码</span>
                                </el-dropdown-item>
                                <el-dropdown-item divided command="logout">
                                    <el-icon>
                                        <SwitchButton />
                                    </el-icon>
                                    <span>退出登录</span>
                                </el-dropdown-item>
                            </el-dropdown-menu>
                        </template>
                    </el-dropdown>
                </template>
                <template v-else>
                    <el-button class="login-button" @click="showLoginDialog = true">
                        登录
                    </el-button>
                </template>
            </div>

            <!-- 移动端菜单按钮 -->
            <div class="mobile-menu-btn" @click="toggleMobileMenu">
                <el-icon size="24">
                    <Menu />
                </el-icon>
            </div>
        </nav>

        <!-- 移动端菜单 -->
        <div class="mobile-menu" :class="{ active: mobileMenuActive }">
            <div class="mobile-menu-content">
                <div class="mobile-menu-header">
                    <span class="mobile-menu-title">菜单</span>
                    <el-icon class="mobile-close-btn" @click="closeMobileMenu">
                        <Close />
                    </el-icon>
                </div>

                <div class="mobile-nav-items">
                    <router-link v-for="item in navItems" :key="item.path" :to="item.path" class="mobile-nav-item"
                        @click="closeMobileMenu" :class="{ active: $route.path === item.path }">
                        <span class="mobile-nav-text">{{ item.name }}</span>
                    </router-link>
                </div>

                <div class="mobile-user-section" v-if="isLoggedIn">
                    <div class="mobile-user-info">
                        <el-avatar :size="48" :src="userAvatar" class="mobile-user-avatar" />
                        <div class="mobile-user-details">
                            <span class="mobile-username">{{ username }}</span>
                            <span class="mobile-user-role">用户</span>
                        </div>
                    </div>
                    <div class="mobile-user-actions">
                        <el-button text @click="handleMobileModifyPassword" class="mobile-action-btn">
                            <el-icon>
                                <Edit />
                            </el-icon>
                            <span>修改密码</span>
                        </el-button>
                        <el-button text @click="handleLogout" class="mobile-action-btn">
                            <el-icon>
                                <SwitchButton />
                            </el-icon>
                            <span>退出登录</span>
                        </el-button>
                    </div>
                </div>
                <div class="mobile-login-section" v-else>
                    <el-button class="mobile-login-button" @click="handleMobileLogin">
                        立即登录
                    </el-button>
                </div>
            </div>
        </div>

        <!-- 主内容区域 -->
        <main class="main-content">
            <!-- 英雄区域 -->
            <section class="hero-section">
                <div class="scrolling-text-container">
                    <div class="text-group">
                        <div class="main-text">
                            <span class="typed-text">{{ displayedMainText }}</span>
                            <span class="cursor">|</span>
                        </div>
                        <div class="sub-text" :class="{ visible: isSubTextVisible }">
                            <span class="typed-text">{{ displayedSubText }}</span>
                            <span class="cursor">|</span>
                        </div>
                    </div>
                </div>

                <!-- AI品牌展示 -->
                <div class="ai-brands-section">
                    <div class="brand-list">
                        <a v-for="brand in aiBrands" :key="brand.name" :href="brand.url" target="_blank"
                            class="brand-item" :style="{ background: brand.gradient }" :title="brand.name">
                            <div class="brand-content">
                                <div class="brand-icon">
                                    <img :src="brand.icon" :alt="brand.name" class="brand-img" />
                                </div>
                                <div class="brand-title">{{ brand.name }}</div>
                                <div class="brand-hover-effect"></div>
                            </div>
                        </a>
                    </div>
                </div>
            </section>
        </main>

        <!-- 底部信息修改 -->
        <footer class="footer">
            <div class="footer-divider"></div>
            <p class="copyright" v-html="copyrightText"></p>
        </footer>

        <!-- 登录对话框 -->
        <el-dialog v-model="showLoginDialog" title="登录" width="400px" :close-on-click-modal="false"
            class="login-dialog">
            <el-form :model="loginForm" :rules="loginRules" ref="loginFormRef" label-width="80px">
                <el-form-item label="账号" prop="username">
                    <el-input v-model="loginForm.username" placeholder="请输入账号" size="large" :prefix-icon="User" />
                </el-form-item>
                <el-form-item label="密码" prop="password">
                    <el-input v-model="loginForm.password" type="password" placeholder="请输入密码" size="large"
                        :prefix-icon="Lock" show-password @keyup.enter="handleLogin" />
                </el-form-item>
            </el-form>
            <template #footer>
                <el-button @click="showLoginDialog = false">取消</el-button>
                <el-button type="primary" @click="handleLogin" :loading="loginLoading" class="login-confirm-btn">
                    {{ loginLoading ? '登录中...' : '登录' }}
                </el-button>
            </template>
        </el-dialog>

        <!-- 修改密码对话框 -->
        <el-dialog v-model="showPasswordDialog" title="修改密码" width="400px" :close-on-click-modal="false"
            class="password-dialog">
            <el-form :model="passwordForm" :rules="passwordRules" ref="passwordFormRef" label-width="100px">
                <el-form-item label="当前密码" prop="oldPassword">
                    <el-input v-model="passwordForm.oldPassword" type="password" placeholder="请输入当前密码" show-password />
                </el-form-item>
                <el-form-item label="新的密码" prop="newPassword">
                    <el-input v-model="passwordForm.newPassword" type="password" placeholder="请输入新的密码" show-password />
                </el-form-item>
                <el-form-item label="确认密码" prop="confirmPassword">
                    <el-input v-model="passwordForm.confirmPassword" type="password" placeholder="请再次输入新密码"
                        show-password />
                </el-form-item>
            </el-form>
            <template #footer>
                <el-button @click="showPasswordDialog = false">取消</el-button>
                <el-button type="primary" @click="handleModifyPassword" class="confirm-btn">确定</el-button>
            </template>
        </el-dialog>
    </div>
</template>

<script>
import { ref, onMounted, computed, watch, inject } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { ArrowDown, Edit, SwitchButton, Menu, Close, User, Lock } from '@element-plus/icons-vue'
import Cookies from 'js-cookie'
import axios from 'axios'
import md5 from 'md5'
import config from '@/config'
import defaultLogo from '@/assets/logo.png'


// 导入品牌图标
import doubaoIcon from '@/assets/doubao.png'
import deepseekIcon from '@/assets/deepseek.png'
import wenxinIcon from '@/assets/wenxin.png'
import yuanbaoIcon from '@/assets/yuanbao.png'
import tongyiIcon from '@/assets/tongyi.png'
import namiIcon from '@/assets/nami.png'

export default {
    name: 'HomePage',
    components: {
        ArrowDown,
        Edit,
        SwitchButton,
        Menu,
        Close
    },
    setup() {
        // 响应式状态
        const isLoggedIn = ref(!!Cookies.get('user_token'))
        const mobileMenuActive = ref(false)
        const showLoginDialog = ref(false)
        const showPasswordDialog = ref(false)
        const loginLoading = ref(false)

        // 监听登录状态变化
        watch(() => Cookies.get('user_token'), (newVal) => {
            isLoggedIn.value = !!newVal
        })
        const webConfig = inject('webConfig', ref({
            shop_name: '全域AI',
            shop_logo: '',
            h5_favicon: '',
            copyright_config: []
        }))

        // 计算属性
        const logoUrl = computed(() => webConfig.value.shop_logo || defaultLogo)
        const copyrightText = computed(() => {
            if (webConfig.value.copyright_config && webConfig.value.copyright_config.length > 0) {
                return webConfig.value.copyright_config.map(item => {
                    if (item.value) {
                        return `<a href="${item.value}" target="_blank" style="color: #ffffff; text-decoration: none;">${item.key}</a>`
                    }
                    return item.key
                }).join(' | ')
            }
            return 'Copyright 全域CEO推广 - Agent 2025 创客号：BICPB123123123号'
        })
        // 导航项
        const navItems = [
            { path: '/', name: '首页' },
            { path: '/ai-portrait', name: 'AI画像' },
            { path: '/ai-auth', name: 'AI授权' },
            { path: '/ai-preparation', name: 'AI备课' },
            { path: '/ai-teaching', name: 'AI授课' },
            { path: '/ai-agent', name: 'AI智能体' },
            { path: '/ai-distillation', name: 'AI蒸馏' },
            { path: '/ai-report', name: 'AI成绩单' }
        ]

        // 用户信息
        const userInfo = computed(() => {
            try {
                return JSON.parse(Cookies.get('user_info') || '{}')
            } catch {
                return {}
            }
        })
        const username = computed(() => userInfo.value.account || '')
        const userAvatar = ref('https://cube.elemecdn.com/3/7c/3ea6beec64369c2642b92c6726f1epng.png')

        // 登录表单
        const loginForm = ref({
            username: '',
            password: ''
        })
        const loginFormRef = ref()
        const loginRules = {
            username: [{ required: true, message: '请输入账号', trigger: 'blur' }],
            password: [{ required: true, message: '请输入密码', trigger: 'blur' }]
        }

        // 打字效果
        const textGroups = [
            {
                mainText: '全域GEO推广, 帮你快速抢占Ai搜索黄金广告位',
                subText: '让客户 一问就有你 一查就信你 一看就找你'
            },
            {
                mainText: '全域AI数字员工，助力企业打造AI数字资产',
                subText: '让AI帮你  推品牌  跑业务  做销售  做客服  做管理'
            }
        ]

        const displayedMainText = ref('')
        const displayedSubText = ref('')
        const currentGroupIndex = ref(0)
        const mainTextIndex = ref(0)
        const subTextIndex = ref(0)
        const isTypingMain = ref(true)
        const isSubTextVisible = ref(false)

        // AI品牌数据
        const aiBrands = [
            {
                name: '豆包',
                icon: doubaoIcon,
                url: 'https://www.doubao.com/chat/search',
                gradient: 'linear-gradient(-125deg, rgb(52, 180, 233), rgb(74, 8, 112))'
            },
            {
                name: 'Deepseek',
                icon: deepseekIcon,
                url: 'https://chat.deepseek.com/',
                gradient: 'linear-gradient(-125deg, rgba(141, 136, 255, 0.7), rgba(57, 15, 143, 0.7))'
            },
            {
                name: '文心一言',
                icon: wenxinIcon,
                url: 'https://yiyan.baidu.com/',
                gradient: 'linear-gradient(-125deg, rgba(140, 71, 212, 0.7), rgba(27, 52, 179, 0.7))'
            },
            {
                name: '元宝',
                icon: yuanbaoIcon,
                url: 'https://yuanbao.tencent.com/',
                gradient: 'linear-gradient(-125deg, rgba(122, 208, 185, 0.7), rgba(14, 37, 173, 0.7))'
            },
            {
                name: '通义',
                icon: tongyiIcon,
                url: 'https://www.tongyi.com/',
                gradient: 'linear-gradient(-125deg, rgba(172, 75, 145, 0.7), rgba(68, 17, 173, 0.7))'
            },
            {
                name: '纳米',
                icon: namiIcon,
                url: 'https://deepseek.n.cn/',
                gradient: 'linear-gradient(-220deg, rgba(172, 75, 145, 0.7), rgb(62, 27, 171))'
            }
        ]

        // 修改密码相关
        const passwordForm = ref({
            oldPassword: '',
            newPassword: '',
            confirmPassword: ''
        })
        const passwordFormRef = ref()
        const passwordRules = {
            oldPassword: [{ required: true, message: '请输入当前密码', trigger: 'blur' }],
            newPassword: [{ required: true, message: '请输入新的密码', trigger: 'blur' }],
            confirmPassword: [{ required: true, message: '请确认新密码', trigger: 'blur' }]
        }

        // 移动端菜单控制
        const toggleMobileMenu = () => {
            mobileMenuActive.value = !mobileMenuActive.value
        }

        const closeMobileMenu = () => {
            mobileMenuActive.value = false
        }

        // 移动端登录
        const handleMobileLogin = () => {
            closeMobileMenu()
            showLoginDialog.value = true
        }

        // 打字机效果
        const startTypingEffect = () => {
            let groupIndex = 0

            const typeNextGroup = () => {
                const group = textGroups[groupIndex]
                mainTextIndex.value = 0
                subTextIndex.value = 0
                displayedMainText.value = ''
                displayedSubText.value = ''
                isTypingMain.value = true
                isSubTextVisible.value = false

                const typeMainText = () => {
                    if (mainTextIndex.value < group.mainText.length) {
                        displayedMainText.value = group.mainText.slice(0, mainTextIndex.value + 1)
                        mainTextIndex.value++

                        // 当主文本输入到一半时，开始显示副文本
                        if (mainTextIndex.value >= Math.floor(group.mainText.length / 2) && !isSubTextVisible.value) {
                            isSubTextVisible.value = true
                        }

                        setTimeout(typeMainText, 120)
                    } else {
                        setTimeout(() => {
                            isTypingMain.value = false
                            typeSubText()
                        }, 300)
                    }
                }

                const typeSubText = () => {
                    if (subTextIndex.value < group.subText.length) {
                        displayedSubText.value = group.subText.slice(0, subTextIndex.value + 1)
                        subTextIndex.value++
                        setTimeout(typeSubText, 80)
                    } else {
                        setTimeout(() => {
                            groupIndex = (groupIndex + 1) % textGroups.length
                            currentGroupIndex.value = groupIndex
                            setTimeout(typeNextGroup, 2000)
                        }, 3000)
                    }
                }

                typeMainText()
            }

            typeNextGroup()
        }

        // 用户命令处理
        const handleUserCommand = (command) => {
            switch (command) {
                case 'modifyPassword':
                    showPasswordDialog.value = true
                    break
                case 'logout':
                    handleLogout()
                    break
            }
        }

        // 移动端修改密码
        const handleMobileModifyPassword = () => {
            closeMobileMenu()
            showPasswordDialog.value = true
        }

        // 登录处理
        const handleLogin = async () => {
            if (!loginFormRef.value) return

            try {
                const valid = await loginFormRef.value.validate()
                if (!valid) return

                loginLoading.value = true

                const response = await axios.post(
                    `${config.BASE_URL}/site/api/login.php`,
                    {
                        account: loginForm.value.username,
                        password: md5(loginForm.value.password)
                    }
                )

                if (response.data.code === 200) {
                    Cookies.set('user_token', response.data.data.id, { expires: 7 })
                    Cookies.set('user_info', JSON.stringify(response.data.data), { expires: 7 })

                    ElMessage.success('登录成功')
                    showLoginDialog.value = false
                    isLoggedIn.value = true
                    loginForm.value = { username: '', password: '' }
                } else {
                    const errorMessages = {
                        400: '请输入完整的账号密码',
                        401: '账号或密码错误',
                        403: response.data.message,
                        500: '服务器错误，请稍后再试'
                    }
                    ElMessage.error(errorMessages[response.data.code] || '登录失败')
                }
            } catch (error) {
                ElMessage.error(error.response ? '登录失败，请重试' : '网络连接异常')
            } finally {
                loginLoading.value = false
            }
        }

        // 修改密码
        const handleModifyPassword = async () => {
            if (!passwordFormRef.value) return

            try {
                const valid = await passwordFormRef.value.validate()
                if (!valid) return

                if (passwordForm.value.newPassword !== passwordForm.value.confirmPassword) {
                    ElMessage.error('两次输入的密码不一致')
                    return
                }

                const response = await axios.post(`${config.BASE_URL}/site/api/shop_api.php`, {
                    action: 'update_password',
                    account: username.value,
                    old_password: passwordForm.value.oldPassword,
                    new_password: passwordForm.value.newPassword
                })

                if (response.data.success) {
                    ElMessage.success('密码修改成功')
                    showPasswordDialog.value = false
                    passwordForm.value = {
                        oldPassword: '',
                        newPassword: '',
                        confirmPassword: ''
                    }
                } else {
                    ElMessage.error(response.data.message || '密码修改失败')
                }
            } catch (error) {
                ElMessage.error('修改密码失败')
            }
        }

        // 退出登录
        const handleLogout = async () => {
            try {
                await ElMessageBox.confirm('确定要退出登录吗？', '提示', {
                    confirmButtonText: '确定',
                    cancelButtonText: '取消',
                    type: 'warning'
                })

                Cookies.remove('user_token')
                Cookies.remove('user_info')
                isLoggedIn.value = false
                ElMessage.success('已退出登录')
                closeMobileMenu()
            } catch {
                // 用户取消
            }
        }

        onMounted(() => {
            startTypingEffect()
        })

        return {
            logoUrl, // 替换原来的 logo
            copyrightText, // 新增
            navItems,
            isLoggedIn,
            username,
            userAvatar,
            displayedMainText,
            displayedSubText,
            isSubTextVisible,
            aiBrands,
            showLoginDialog,
            showPasswordDialog,
            loginForm,
            loginFormRef,
            loginRules,
            loginLoading,
            passwordForm,
            passwordFormRef,
            passwordRules,
            mobileMenuActive,
            handleUserCommand,
            handleLogin,
            handleModifyPassword,
            handleMobileModifyPassword,
            handleMobileLogin,
            handleLogout,
            toggleMobileMenu,
            closeMobileMenu,
            User,
            Lock
        }
    }
}
</script>

<style scoped lang="scss">
.home-container {
    min-height: 100vh;
    position: relative;
    overflow-x: hidden;
    display: flex;
    flex-direction: column;
}

/* 底部链接样式 */
.copyright {
    a {
        color: #ffffff !important;
        text-decoration: none;
        transition: color 0.3s ease;

        &:hover {
            color: #6e21fe !important;
            text-decoration: underline;
        }
    }
}

// 背景效果
.home-container::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background:
        radial-gradient(circle at 20% 80%, rgba(110, 33, 254, 0.1) 0%, transparent 50%),
        radial-gradient(circle at 80% 20%, rgba(139, 92, 246, 0.08) 0%, transparent 50%),
        radial-gradient(circle at 40% 40%, rgba(192, 132, 252, 0.05) 0%, transparent 50%);
    pointer-events: none;
    z-index: 0;
}

.home-container::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image:
        linear-gradient(rgba(110, 33, 254, 0.03) 1px, transparent 1px),
        linear-gradient(90deg, rgba(110, 33, 254, 0.03) 1px, transparent 1px);
    background-size: 50px 50px;
    pointer-events: none;
    z-index: 0;
}

// 导航栏
.navbar {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    height: 70px;
    background: rgba(13, 5, 26, 0.95);
    backdrop-filter: blur(15px);
    border-bottom: 1px solid rgba(110, 33, 254, 0.3);
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 40px;
    z-index: 1000;
    box-shadow: 0 4px 30px rgba(0, 0, 0, 0.5);

    .nav-brand {
        display: flex;
        align-items: center;

        .logo {
            height: 45px;
            width: auto;
            border-radius: 8px;
        }
    }

    .nav-menu {
        display: flex;
        align-items: center;
        gap: 8px;

        .nav-item {
            padding: 12px 20px;
            color: #a0a8b8;
            text-decoration: none;
            border-radius: 8px;
            transition: all 0.3s ease;
            font-weight: 500;
            position: relative;
            white-space: nowrap;

            &:hover {
                color: #6e21fe;
                background: rgba(110, 33, 254, 0.1);
            }

            &.active {
                color: #ffffff;
                background: #6e21fe !important;
                box-shadow: 0 4px 15px rgba(110, 33, 254, 0.4);

                &::after {
                    content: '';
                    position: absolute;
                    bottom: -11px;
                    left: 50%;
                    transform: translateX(-50%);
                    width: 0;
                    height: 0;
                    border-left: 6px solid transparent;
                    border-right: 6px solid transparent;
                    border-bottom: 6px solid #6e21fe;
                }
            }
        }
    }

    .nav-actions {
        .login-button {
            padding: 10px 24px;
            background: linear-gradient(135deg, #6e21fe 0%, #8b5cf6 100%);
            color: white;
            border: none;
            border-radius: 8px;
            font-weight: 600;
            transition: all 0.3s ease;
            box-shadow: 0 4px 15px rgba(110, 33, 254, 0.4);

            &:hover {
                transform: translateY(-2px);
                box-shadow: 0 8px 25px rgba(110, 33, 254, 0.6);
            }
        }

        .user-info {
            display: flex;
            align-items: center;
            gap: 12px;
            padding: 8px 16px;
            border-radius: 8px;
            cursor: pointer;
            transition: all 0.3s ease;
            background: rgba(255, 255, 255, 0.05);
            border: 1px solid rgba(110, 33, 254, 0.2);

            &:hover {
                background: rgba(110, 33, 254, 0.1);
                border-color: rgba(110, 33, 254, 0.4);
            }

            .username {
                color: #e0e6ff;
                font-weight: 600;
                font-size: 14px;
            }

            .arrow {
                color: #a0a8b8;
                font-size: 12px;
            }
        }
    }

    .mobile-menu-btn {
        display: none;
        cursor: pointer;
        color: #e0e6ff;
        padding: 8px;
        border-radius: 6px;
        transition: all 0.3s ease;
        z-index: 1002;

        &:hover {
            background: rgba(110, 33, 254, 0.1);
        }
    }
}

// 移动端菜单
.mobile-menu {
    position: fixed;
    top: 0;
    right: -100%;
    width: 85vw;
    max-width: 200px;
    height: 100vh;
    background: rgba(13, 5, 26, 0.98);
    backdrop-filter: blur(20px);
    border-left: 1px solid rgba(110, 33, 254, 0.3);
    z-index: 1001;
    transition: right 0.3s ease;
    display: flex;

    &.active {
        right: 0;
    }

    .mobile-menu-content {
        flex: 1;
        padding: 0;
        display: flex;
        flex-direction: column;
        overflow-y: auto;
        width: 100%;

        .mobile-menu-header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 20px;
            border-bottom: 1px solid rgba(110, 33, 254, 0.2);
            background: rgba(110, 33, 254, 0.1);

            .mobile-menu-title {
                color: #e0e6ff;
                font-size: 18px;
                font-weight: 600;
            }

            .mobile-close-btn {
                color: #a0a8b8;
                cursor: pointer;
                padding: 4px;
                width: 40px;
                height: 40px;
                border-radius: 4px;
                transition: all 0.3s ease;

                &:hover {
                    color: #6e21fe;
                    background: rgba(110, 33, 254, 0.1);
                }
            }
        }

        .mobile-nav-items {
            flex: 1;
            padding: 20px;
        }

        .mobile-nav-item {
            display: flex;
            align-items: center;
            padding: 15px 20px;
            color: #a0a8b8;
            text-decoration: none;
            border-radius: 8px;
            margin-bottom: 8px;
            transition: all 0.3s ease;
            font-weight: 500;
            text-align: left;
            width: 100%;

            &:hover,
            &.active {
                color: #ffffff;
                background: #6e21fe;
                box-shadow: 0 4px 15px rgba(110, 33, 254, 0.4);
            }

            .mobile-nav-text {
                flex: 1;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
            }
        }

        .mobile-user-section {
            margin-top: auto;
            padding: 20px;
            border-top: 1px solid rgba(255, 255, 255, 0.1);
            background: rgba(255, 255, 255, 0.05);

            .mobile-user-info {
                display: flex;
                align-items: center;
                gap: 12px;
                margin-bottom: 20px;

                .mobile-user-avatar {
                    flex-shrink: 0;
                    border: 2px solid rgba(110, 33, 254, 0.3);
                }

                .mobile-user-details {
                    display: flex;
                    flex-direction: column;
                    gap: 4px;

                    .mobile-username {
                        color: #e0e6ff;
                        font-weight: 600;
                        font-size: 16px;
                    }

                    .mobile-user-role {
                        color: #a0a8b8;
                        font-size: 12px;
                    }
                }
            }

            .mobile-user-actions {
                display: flex;
                flex-direction: column;
                gap: 8px;

                .mobile-action-btn {
                    justify-content: flex-start !important;
                    padding: 12px 0 !important;
                    width: 100%;
                    margin-left: 0 !important;
                    color: #a0a8b8;

                    &:hover {
                        color: #6e21fe;
                        background: rgba(110, 33, 254, 0.1);
                    }

                    span {
                        display: flex;
                        align-items: center;
                        gap: 8px;
                        justify-content: flex-start;
                        width: 100%;
                    }
                }
            }
        }

        .mobile-login-section {
            margin-top: auto;
            padding: 20px;
            border-top: 1px solid rgba(255, 255, 255, 0.1);

            .mobile-login-button {
                width: 100%;
                padding: 12px 20px;
                background: linear-gradient(135deg, #6e21fe 0%, #8b5cf6 100%);
                color: white;
                border: none;
                border-radius: 8px;
                text-align: center;
                font-weight: 600;
                transition: all 0.3s ease;

                &:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 8px 25px rgba(110, 33, 254, 0.6);
                }
            }
        }
    }


}

// 主内容
.main-content {
    position: relative;
    z-index: 1;
    padding-top: 70px;
    min-height: calc(100vh - 160px);
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 1;
}

.hero-section {
    text-align: center;
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 20px;
    width: 100%;
}

// 优化打字效果容器
.scrolling-text-container {
    min-height: 200px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 60px;

    .text-group {
        width: 100%;

        .main-text {
            font-size: 2.8rem;
            font-weight: 700;
            margin-bottom: 25px;
            background: linear-gradient(135deg, #6e21fe 0%, #8b5cf6 100%);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            text-shadow: 0 2px 20px rgba(110, 33, 254, 0.3);
            line-height: 1.4;
            min-height: 1.4em;
            letter-spacing: 1px;
            word-spacing: 2px;

            .cursor {
                animation: blink 1s infinite;
                color: #6e21fe;
                margin-left: 2px;
            }
        }

        .sub-text {
            font-size: 1.4rem;
            color: #cbd5e1;
            font-weight: 500;
            line-height: 1.5;
            min-height: 1.5em;
            letter-spacing: 0.5px;
            word-spacing: 1px;
            opacity: 0;
            transform: translateY(10px);
            transition: all 0.5s ease;

            &.visible {
                opacity: 1;
                transform: translateY(0);
            }

            .cursor {
                animation: blink 1s infinite;
                color: #cbd5e1;
                margin-left: 2px;
            }
        }
    }
}

@keyframes blink {

    0%,
    50% {
        opacity: 1;
    }

    51%,
    100% {
        opacity: 0;
    }
}

// AI品牌展示
.ai-brands-section {
    margin-top: 40px;
    width: 100%;

    .brand-list {
        display: flex;
        justify-content: center;
        align-items: center;
        gap: 20px;
        flex-wrap: wrap;
        max-width: 1000px;
        margin: 0 auto;

        .brand-item {
            display: block;
            text-decoration: none;
            border-radius: 10px;
            overflow: hidden;
            transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
            position: relative;
            box-shadow:
                0 8px 30px rgba(0, 0, 0, 0.3),
                0 2px 8px rgba(110, 33, 254, 0.2);
            border: 1px solid rgba(255, 255, 255, 0.2);
            width: 150px;
            height: 160px;
            flex-shrink: 0;

            &::before {
                content: '';
                position: absolute;
                top: 0;
                left: 0;
                right: 0;
                bottom: 0;
                background: linear-gradient(135deg,
                        rgba(255, 255, 255, 0.1) 0%,
                        rgba(255, 255, 255, 0) 50%);
                opacity: 0;
                transition: opacity 0.3s ease;
            }

            &:hover {
                transform: translateY(-8px) scale(1.08);
                box-shadow:
                    0 20px 50px rgba(110, 33, 254, 0.6),
                    0 0 0 2px rgba(255, 255, 255, 0.3),
                    0 0 30px rgba(110, 33, 254, 0.8);
                z-index: 10;

                &::before {
                    opacity: 1;
                }

                .brand-content {
                    .brand-icon {
                        transform: scale(1.2) translateY(-3px);
                    }

                    .brand-title {
                        color: #ffffff;
                        text-shadow:
                            0 2px 10px rgba(0, 0, 0, 0.8),
                            0 0 15px rgba(255, 255, 255, 0.4);
                        font-weight: 700;
                        transform: translateY(2px);
                    }
                }

                .brand-hover-effect {
                    opacity: 1;
                    transform: scale(1);
                }
            }

            .brand-content {
                position: relative;
                width: 100%;
                height: 100%;
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                padding: 20px 15px;
                z-index: 2;

                .brand-icon {
                    flex: 1;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    margin-bottom: 12px;
                    transition: all 0.4s ease;

                    .brand-img {
                        width: 50px;
                        height: 50px;
                        object-fit: contain;
                        opacity: 0.9;
                    }
                }

                .brand-title {
                    position: relative;
                    z-index: 2;
                    color: #ffffff;
                    font-size: 15px;
                    font-weight: 600;
                    text-align: center;
                    transition: all 0.3s ease;
                    margin-top: auto;
                }

                .brand-hover-effect {
                    position: absolute;
                    top: -2px;
                    left: -2px;
                    right: -2px;
                    bottom: -2px;
                    background: radial-gradient(circle at center,
                            rgba(255, 255, 255, 0.3) 0%,
                            rgba(255, 255, 255, 0) 70%);
                    border-radius: 18px;
                    opacity: 0;
                    transform: scale(0.8);
                    transition: all 0.4s ease;
                    z-index: 1;
                }
            }
        }
    }
}

// 底部信息
.footer {
    position: relative;
    z-index: 1;
    text-align: center;
    padding: 30px 20px;
    margin-top: auto;

    .footer-divider {
        border-top: 1px dashed #3a3a3e;
        margin: 0 0 20px 0;
    }

    .copyright {
        margin: 0;
        color: #ffffff;
        font-size: 16px;
        font-weight: 500;
        line-height: 1.5;
    }
}

// 登录对话框样式
.login-dialog {
    :deep(.el-dialog) {
        background: rgba(13, 5, 26, 0.95) !important;
        backdrop-filter: blur(20px);
        border: 1px solid rgba(255, 112, 72, 0.3);
        border-radius: 16px;
        box-shadow:
            0 20px 60px rgba(0, 0, 0, 0.5),
            0 0 0 1px rgba(255, 112, 72, 0.1);

        .el-dialog__header {
            background: rgba(255, 112, 72, 0.1);
            border-bottom: 1px solid rgba(255, 112, 72, 0.2);
            margin: 0;
            padding: 20px;

            .el-dialog__title {
                color: #e0e6ff !important;
                font-weight: 600;
                background: linear-gradient(135deg, #ff7048 0%, #ff9d76 100%);
                -webkit-background-clip: text;
                -webkit-text-fill-color: transparent;
            }
        }

        .el-dialog__body {
            padding: 30px;

            .el-form-item__label {
                color: #e0e6ff !important;
                font-weight: 600 !important;
                font-size: 14px !important;
                margin-bottom: 8px !important;
                display: block !important;
            }

            .el-input__wrapper {
                background: rgba(255, 255, 255, 0.08);
                backdrop-filter: blur(10px);
                border: 1px solid rgba(255, 112, 72, 0.3);
                border-radius: 8px;
                transition: all 0.3s ease;

                &:hover {
                    border-color: #ff7048;
                    box-shadow: 0 4px 15px rgba(255, 112, 72, 0.2);
                }

                &.is-focus {
                    border-color: #ff7048;
                    box-shadow: 0 4px 20px rgba(255, 112, 72, 0.3);
                }

                .el-input__inner {
                    color: #e8e9ec !important;
                    background: transparent !important;

                    &::placeholder {
                        color: #64748b !important;
                    }
                }
            }

            :deep(.el-icon) {
                color: #ff7048;
            }
        }

        .el-dialog__footer {
            padding: 20px 30px 30px;
            border-top: 1px solid rgba(255, 112, 72, 0.1);

            .el-button {
                border-radius: 8px;
                font-weight: 600;
                transition: all 0.3s ease;

                &.login-confirm-btn {
                    background: linear-gradient(135deg, #ff7048 0%, #ff9d76 100%);
                    border: none;
                    box-shadow: 0 4px 15px rgba(255, 112, 72, 0.3);

                    &:hover {
                        transform: translateY(-2px);
                        box-shadow: 0 8px 25px rgba(255, 112, 72, 0.4);
                    }
                }
            }
        }
    }
}

// 修改密码对话框样式
.password-dialog {
    :deep(.el-dialog) {
        background: rgba(13, 5, 26, 0.95) !important;
        backdrop-filter: blur(20px);
        border: 1px solid rgba(110, 33, 254, 0.3);
        border-radius: 16px;
        box-shadow:
            0 20px 60px rgba(0, 0, 0, 0.5),
            0 0 0 1px rgba(110, 33, 254, 0.1);

        .el-dialog__header {
            background: rgba(110, 33, 254, 0.1);
            border-bottom: 1px solid rgba(110, 33, 254, 0.2);
            margin: 0;
            padding: 20px;
            ;

            .el-dialog__title {
                color: #e0e6ff !important;
                font-weight: 600;
                background: linear-gradient(135deg, #6e21fe 0%, #8b5cf6 100%);
                -webkit-background-clip: text;
                -webkit-text-fill-color: transparent;
            }
        }

        .el-dialog__body {
            padding: 30px;

            .el-form-item__label {
                color: #e0e6ff !important;
                font-weight: 600 !important;
                font-size: 14px !important;
                margin-bottom: 8px !important;
                display: block !important;
            }

            .el-input__wrapper {
                background: rgba(255, 255, 255, 0.08);
                backdrop-filter: blur(10px);
                border: 1px solid rgba(110, 33, 254, 0.3);
                border-radius: 8px;
                transition: all 0.3s ease;

                &:hover {
                    border-color: #6e21fe;
                    box-shadow: 0 4px 15px rgba(110, 33, 254, 0.2);
                }

                &.is-focus {
                    border-color: #6e21fe;
                    box-shadow: 0 4px 20px rgba(110, 33, 254, 0.3);
                }

                .el-input__inner {
                    color: #e0e6ff !important;
                    background: transparent !important;

                    &::placeholder {
                        color: #64748b !important;
                    }
                }
            }
        }

        .el-dialog__footer {
            padding: 20px 30px 30px;
            border-top: 1px solid rgba(110, 33, 254, 0.1);

            .el-button {
                border-radius: 8px;
                font-weight: 600;
                transition: all 0.3s ease;

                &.confirm-btn {
                    background: linear-gradient(135deg, #6e21fe 0%, #8b5cf6 100%);
                    border: none;
                    box-shadow: 0 4px 15px rgba(110, 33, 254, 0.3);

                    &:hover {
                        transform: translateY(-2px);
                        box-shadow: 0 8px 25px rgba(110, 33, 254, 0.4);
                    }
                }
            }
        }
    }
}

// 响应式设计
@media (max-width: 1024px) {
    .navbar {
        .nav-menu {
            gap: 4px;

            .nav-item {
                padding: 10px 16px;
                font-size: 14px;
            }
        }
    }

    .scrolling-text-container .text-group {
        .main-text {
            font-size: 2.4rem;
        }

        .sub-text {
            font-size: 1.2rem;
        }
    }

    .ai-brands-section .brand-list {
        gap: 15px;

        .brand-item {
            width: 130px;
            height: 120px;

            .brand-content {
                .brand-icon .brand-img {
                    width: 45px;
                    height: 45px;
                }

                .brand-title {
                    font-size: 14px;
                }
            }
        }
    }
}

@media (max-width: 768px) {
    .navbar {
        padding: 0 20px;

        .nav-menu {
            display: none;
        }

        .nav-brand .logo {
            height: 35px;
        }

        .mobile-menu-btn {
            display: block;
        }
    }

    .scrolling-text-container {
        min-height: 150px;
        margin-bottom: 40px;

        .text-group {
            .main-text {
                font-size: 2rem;
            }

            .sub-text {
                font-size: 1.1rem;
            }
        }
    }

    .ai-brands-section .brand-list {
        gap: 12px;

        .brand-item {
            width: 110px;
            height: 100px;

            .brand-content {
                padding: 15px 10px;

                .brand-icon .brand-img {
                    width: 40px;
                    height: 40px;
                }

                .brand-title {
                    font-size: 13px;
                }
            }
        }
    }

    .footer {
        padding: 25px 15px;

        .copyright {
            font-size: 14px;
        }
    }
}

@media (max-width: 480px) {
    .scrolling-text-container .text-group {
        .main-text {
            font-size: 1.6rem;
        }

        .sub-text {
            font-size: 1rem;
        }
    }

    .ai-brands-section .brand-list {
        gap: 10px;

        .brand-item {
            width: 100px;
            height: 90px;

            .brand-content {
                padding: 12px 8px;

                .brand-icon .brand-img {
                    width: 35px;
                    height: 35px;
                }

                .brand-title {
                    font-size: 12px;
                }
            }
        }
    }

    .footer {
        padding: 20px 10px;

        .copyright {
            font-size: 13px;
        }
    }
}

/* 强制移动端菜单显示修复 */
@media (max-width: 768px) {
    .navbar .mobile-menu-btn {
        display: block !important;
    }

    .mobile-menu.active {
        right: 0 !important;
        visibility: visible !important;
        opacity: 1 !important;
    }

    .mobile-menu:not(.active) {
        right: -100% !important;
        visibility: hidden !important;
        opacity: 0 !important;
    }
}

/* 确保移动端菜单在最上层 */
.mobile-menu {
    z-index: 1001 !important;
}

.navbar .mobile-menu-btn {
    z-index: 1002 !important;
}
</style>

<style>
/* 输入框文字颜色 */
.login-dialog .el-input__inner,
.password-dialog .el-input__inner {
    color: #ffffff !important;
}


/* 登录对话框样式 */
.login-dialog .el-form-item__label,
.login-dialog .el-dialog__title {
    color: #ffffff !important;
}

/* 修改密码对话框样式 */
.password-dialog .el-form-item__label,
.password-dialog .el-dialog__title {
    color: #ffffff !important;
}

/* 使用深度选择器确保生效 */
:deep(.login-dialog) .el-form-item__label,
:deep(.login-dialog) .el-dialog__title,
:deep(.password-dialog) .el-form-item__label,
:deep(.password-dialog) .el-dialog__title {
    color: #ffffff !important;
}
</style>